import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetAutomationSettingFieldsQuery } from '@/middlewares/defaultApi';
import type { RootState } from '@/reducers/reducers';
import { selectAppSettingAutomations, selectIsDemoInstanceOrDemoModeEnabled } from '@/selectors/selectors';
import { demoAutomationSettingFieldsByType } from '@/testData/automationSettingFields';
import type { SettingField } from '@/types';

// Fields are keyed by automation id, since an automation's step decides its options; automations the FE still builds have none
export const useAutomationSettingFields = (
    orgId: string,
    appId: string,
    // Bucket automations load their fields from the bucket
    skip = false,
): {
    settingFieldsByAutomationId: Record<string, SettingField[]> | undefined;
    isSettingFieldsError: boolean;
} => {
    const isDemoMode = useSelector((state: RootState) => selectIsDemoInstanceOrDemoModeEnabled(state, orgId));
    const { data, isError } = useGetAutomationSettingFieldsQuery(
        { orgId, appId },
        { refetchOnMountOrArgChange: false, skip: skip || isDemoMode },
    );

    // Demo apps aren't stored, so the endpoint can't serve them
    const automations = useSelector((state: RootState) => selectAppSettingAutomations(state, appId));
    const demoSettingFieldsByAutomationId = useMemo(() => {
        const fieldsByAutomationId: Record<string, SettingField[]> = {};
        for (const automation of automations ?? []) {
            const fields = demoAutomationSettingFieldsByType[automation.type];
            if (fields) {
                fieldsByAutomationId[automation.id] = fields;
            }
        }
        return fieldsByAutomationId;
    }, [automations]);

    return {
        settingFieldsByAutomationId: isDemoMode ? demoSettingFieldsByAutomationId : data?.data,
        isSettingFieldsError: isError,
    };
};
